// src/types/attendance.ts

export interface WorkShift {
  id: string;
  name: string;
  start_time: string;
  end_time: string;
  work_days: number[]; // e.g. [1, 2, 3, 4, 5] where 0 is Sunday
  company_id: string;
  employees?: {
    id: string;
    name: string;
    identification: string;
  }[];
}

export interface CreateShiftRequest {
  slug: string;
  name: string;
  start_time: string;
  end_time: string;
  work_days: number[];
}

export interface Holiday {
  id: string;
  date: string;
  description: string;
}

export interface CreateHolidayRequest {
  slug: string;
  date: string;
  description: string;
}

export interface EmployeeSchedule {
  id: string;
  employee_id: string;
  work_shift_id: string;
  start_date: string;
  end_date: string;
  employee?: {
    id: string;
    name: string;
  };
  workShift?: {
    id: string;
    name: string;
    start_time: string;
    end_time: string;
    work_days?: number[];
  };
}

export interface CreateScheduleRequest {
  employee_id: string;
  work_shift_id: string;
  start_date: string;
  end_date: string;
  slug: string;
  company_id: string;
}

export interface BulkAssignScheduleRequest {
  employee_ids: string[];
  work_shift_id: string;
  start_date: string;
  end_date: string;
  slug: string;
  company_id: string;
}

export const days = [
  { id: 1, label: "Lun" },
  { id: 2, label: "Mar" },
  { id: 3, label: "Mie" },
  { id: 4, label: "Jue" },
  { id: 5, label: "Vie" },
  { id: 6, label: "Sab" },
  { id: 0, label: "Dom" },
];

export interface AttendanceRecord {
  id: string;
  employee_id: string;
  employee_name?: string;
  employee?: {
    name: string;
    identification: string;
  };
  date: string;
  check_in: string;
  check_out: string;
  scheduled_start_time?: string | null;
  scheduled_end_time?: string | null;
  overtime_minutes: number;
  status: string;
}

export interface UploadBatchResult {
  identification: string;
  date: string;
  status: "success" | "error";
  message?: string;
}

// --- ABSENCES ---
export type AbsenceType = 'Vacation' | 'Permission' | 'Medical';
export type AbsenceStatus = 'Pending' | 'Approved' | 'Rejected';

export interface Absence {
  id: string;
  employee_id: string;
  company_id: string;
  slug: string;
  type: AbsenceType;
  start_date: string;
  end_date: string;
  description?: string;
  status: AbsenceStatus;
  employee?: {
    id: string;
    name: string;
    identification: string;
  };
}

export interface CreateAbsenceRequest {
  employee_id: string;
  type: AbsenceType;
  start_date: string;
  end_date: string;
  description?: string;
  slug: string;
  company_id: string;
}

// --- PERFORMANCE & QUALITY ---
export interface PerformanceRanking {
  id: string;
  name: string;
  identification: string;
  position: string;
  area: string;
  score: number;
  averageScore: number;
  totalEvaluations: number;
  coveredStations: number;
  latestScore: number;
  scoreTrend: number;
  qualityLevel: string;
  lastEvaluationDate: string | null;
  lastWorkstationName: string | null;
}

export interface PerformanceFilters {
  start_date?: string;
  end_date?: string;
}

export interface QualityIncident {
  id: string;
  employee_id: string;
  severity: 'Low' | 'Medium' | 'High';
  description: string;
  date: string;
  status: 'Reported' | 'Resolved';
}

export interface ProductionTask {
  id: string;
  employee_id: string;
  name: string;
  status: 'Pending' | 'Completed' | 'Cancelled';
  date: string;
}

export interface WorkstationEvaluation {
  id: string;
  employee_id: string;
  company_id?: string;
  slug?: string;
  workstation_name: string;
  score: number;
  max_score: number;
  evaluation_date: string;
  evaluator_name?: string | null;
  notes?: string | null;
}

export interface WorkstationEvaluationHistoryItem {
  id: string;
  workstationName: string;
  score: number;
  maxScore: number;
  normalizedScore: number;
  evaluationDate: string;
  evaluatorName?: string | null;
  notes?: string | null;
}

export interface WorkstationStationBreakdown {
  workstationName: string;
  evaluationsCount: number;
  averageScore: number;
  latestScore: number;
  lastEvaluationDate: string | null;
}

export interface WorkstationEvaluationHistoryResponse {
  employee: {
    id: string;
    name: string;
    identification: string;
    position: string;
    area: string;
  };
  summary: {
    averageScore: number;
    qualityLevel: string;
    totalEvaluations: number;
    coveredStations: number;
    bestStationName: string | null;
    worstStationName: string | null;
  };
  stationBreakdown: WorkstationStationBreakdown[];
  evaluations: WorkstationEvaluationHistoryItem[];
}

export interface IncidentPerformanceScore {
  employee: {
    id: string;
    name: string;
    identification: string;
    position: string;
    area: string;
  };
  highIncidents: number;
  completedTasks: number;
  score: number;
  performanceLevel: string;
  incidentRate: number;
  scoreFormula: string;
}

export interface CreateWorkstationEvaluationRequest {
  employee_id: string;
  company_id: string;
  slug: string;
  workstation_name: string;
  score: number;
  max_score?: number;
  evaluation_date: string;
  evaluator_name?: string;
  notes?: string;
}
